/**
 * FluxNode Profitability Calculator
 *
 * Calculates ROI including:
 * - Hardware costs (server purchase)
 * - Hosting costs (third-party vs self-hosted)
 * - Collateral purchase price
 * - FLUX rewards
 * - Price evolution simulation
 */

import { useState, useMemo, useEffect } from 'react';
import { Icons } from '../Icons';
import type { FluxNode } from '../../types/fluxNode.types';
import type { NodePurchaseTracking } from '../../pages/FluxNodeMonitor';

// Node tier configurations
const NODE_TIERS = {
  CUMULUS: { collateral: 1000, label: 'CUMULUS', color: '#3b82f6' },
  NIMBUS: { collateral: 12500, label: 'NIMBUS', color: '#8b5cf6' },
  STRATUS: { collateral: 40000, label: 'STRATUS', color: '#ec4899' }
} as const;

type NodeTier = keyof typeof NODE_TIERS;

interface CalculatorConfig {
  // Node configuration
  tier: NodeTier;
  nodeCount: number;

  // Hardware costs
  serverCost: number; // One-time server purchase cost (€)
  upsCost: number; // UPS/onduleur cost (€)
  maintenancePercent: number; // Annual maintenance as % of server cost

  // Hosting costs - per tier (monthly cost per node)
  thirdPartyHostingCosts: Record<NodeTier, number>; // €/mois/node par tier
  selfHostedElectricity: number; // Monthly electricity cost if self-hosted (€)
  selfHostedInternet: number; // Monthly internet cost portion (€)
  coolingCost: number; // Monthly cooling/AC cost (€)
  fixedIpCost: number; // Monthly fixed IP cost (€)

  // Collateral - now computed from nodePurchaseTracking
  avgPurchasePrice: number; // Average FLUX purchase price ($) - computed or manual
  exchangeFeePercent: number; // Exchange fees when buying FLUX (%)

  // Rewards (FLUX per day per node)
  dailyRewardPerNode: number;
  rewardDecreasePercent: number; // Annual reward decrease due to network growth (%)

  // Simulation
  priceGrowthScenario: 'bearish' | 'neutral' | 'bullish';
  customPriceGrowth: number; // Custom price growth percentage
}

interface FluxNodeProfitCalculatorProps {
  fluxPrice: number;
  realRewards?: {
    byTier: {
      CUMULUS: { daily: number };
      NIMBUS: { daily: number };
      STRATUS: { daily: number };
    };
  };
  // New props for collateral tracking
  nodes?: FluxNode[];
  nodePurchaseTracking?: NodePurchaseTracking;
}

// View mode type for progressive disclosure
type ViewMode = 'essential' | 'standard' | 'expert';

export function FluxNodeProfitCalculator({
  fluxPrice,
  realRewards,
  nodes = [],
  nodePurchaseTracking = {}
}: FluxNodeProfitCalculatorProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [useManualPrice, setUseManualPrice] = useState(false);
  const [simulatedPrice, setSimulatedPrice] = useState<number | null>(null); // null = use real price
  const [proMode, setProMode] = useState(false);
  const [showScenariosPanel, setShowScenariosPanel] = useState(false);

  // View mode for progressive disclosure (Essentiel/Standard/Expert)
  const [viewMode, setViewMode] = useState<ViewMode>('standard');

  // Collapsible sections state
  const [sections, setSections] = useState({
    config: true,        // Configuration form
    results: true,       // Key metrics
    chart: false,        // Breakeven chart (collapsed by default)
    comparison: false,   // Tier comparison (collapsed by default)
    risk: false,         // Risk analysis (collapsed by default)
    details: false       // Detailed breakdown table (collapsed by default)
  });

  const toggleSection = (section: keyof typeof sections) => {
    setSections(prev => ({ ...prev, [section]: !prev[section] }));
  };
  const [savedScenarios, setSavedScenarios] = useState<Array<{
    id: string;
    name: string;
    date: string;
    config: CalculatorConfig;
    proConfig?: typeof proConfig;
    proMode: boolean;
  }>>([]);
  const [scenarioName, setScenarioName] = useState('');

  // Load saved scenarios from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('fluxnode_calculator_scenarios');
    if (saved) {
      try {
        setSavedScenarios(JSON.parse(saved));
      } catch (e) {
        console.error('Error loading scenarios:', e);
      }
    }
  }, []);

  // Save scenario
  const saveScenario = () => {
    const name = scenarioName.trim() || `Scénario ${savedScenarios.length + 1}`;
    const newScenario = {
      id: Date.now().toString(),
      name,
      date: new Date().toISOString(),
      config: { ...config },
      proConfig: proMode ? { ...proConfig } : undefined,
      proMode
    };
    const updated = [...savedScenarios, newScenario];
    setSavedScenarios(updated);
    localStorage.setItem('fluxnode_calculator_scenarios', JSON.stringify(updated));
    setScenarioName('');
  };

  // Load scenario
  const loadScenario = (scenario: typeof savedScenarios[0]) => {
    setConfig(scenario.config);
    if (scenario.proConfig) {
      setProConfig(scenario.proConfig as typeof proConfig);
    }
    setProMode(scenario.proMode);
    setShowScenariosPanel(false);
  };

  // Delete scenario
  const deleteScenario = (id: string) => {
    const updated = savedScenarios.filter(s => s.id !== id);
    setSavedScenarios(updated);
    localStorage.setItem('fluxnode_calculator_scenarios', JSON.stringify(updated));
  };

  const [proConfig, setProConfig] = useState({
    serverCount: 1,
    vatRecoverable: false,
    vatRate: 20,
    depreciationType: 'linear' as 'linear' | 'degressive',
    wattage: 150, // Server power consumption in watts
    electricityPriceKwh: 0.25, // Price per kWh
    insuranceCost: 0, // Monthly insurance
    mixedTiers: false,
    cumulusCount: 0,
    nimbusCount: 0,
    stratusCount: 2,
  });
  const [config, setConfig] = useState<CalculatorConfig>({
    tier: 'STRATUS',
    nodeCount: 2,
    serverCost: 6000,
    upsCost: 300,
    maintenancePercent: 5,
    thirdPartyHostingCosts: {
      CUMULUS: 15,   // Default estimate for CUMULUS
      NIMBUS: 30,    // Default estimate for NIMBUS
      STRATUS: 54,   // Known price for STRATUS
    },
    selfHostedElectricity: 30,
    selfHostedInternet: 10,
    coolingCost: 15,
    fixedIpCost: 5,
    avgPurchasePrice: 0.50,
    exchangeFeePercent: 0.5,
    dailyRewardPerNode: realRewards?.byTier?.STRATUS?.daily || 8,
    rewardDecreasePercent: 10,
    priceGrowthScenario: 'neutral',
    customPriceGrowth: 20
  });

  // Calculate weighted average purchase price from nodePurchaseTracking
  const trackedCollateralData = useMemo(() => {
    if (!nodes.length || !Object.keys(nodePurchaseTracking).length) {
      return null;
    }

    // Group nodes by tier and calculate weighted average
    const tierData: Record<NodeTier, { totalCollateral: number; totalCost: number; count: number }> = {
      CUMULUS: { totalCollateral: 0, totalCost: 0, count: 0 },
      NIMBUS: { totalCollateral: 0, totalCost: 0, count: 0 },
      STRATUS: { totalCollateral: 0, totalCost: 0, count: 0 }
    };

    nodes.forEach(node => {
      const nodeKey = `${node.txhash}:${node.outidx}`;
      const tracking = nodePurchaseTracking[nodeKey];

      if (tracking && tracking.purchasePrice > 0) {
        const tier = node.tier as NodeTier;
        const collateral = NODE_TIERS[tier].collateral;

        tierData[tier].totalCollateral += collateral;
        tierData[tier].totalCost += collateral * tracking.purchasePrice;
        tierData[tier].count += 1;
      }
    });

    // Calculate overall weighted average
    const totalCollateral = Object.values(tierData).reduce((sum, t) => sum + t.totalCollateral, 0);
    const totalCost = Object.values(tierData).reduce((sum, t) => sum + t.totalCost, 0);
    const weightedAvgPrice = totalCollateral > 0 ? totalCost / totalCollateral : 0;

    return {
      tierData,
      totalCollateral,
      totalCost,
      weightedAvgPrice,
      trackedNodesCount: Object.values(tierData).reduce((sum, t) => sum + t.count, 0)
    };
  }, [nodes, nodePurchaseTracking]);

  // Count nodes by tier from actual data
  const nodeCountByTier = useMemo(() => {
    const counts: Record<NodeTier, number> = { CUMULUS: 0, NIMBUS: 0, STRATUS: 0 };
    nodes.forEach(node => {
      const tier = node.tier as NodeTier;
      if (counts[tier] !== undefined) {
        counts[tier]++;
      }
    });
    return counts;
  }, [nodes]);

  // Auto-update config when tracked data changes
  useEffect(() => {
    if (!useManualPrice && trackedCollateralData && trackedCollateralData.weightedAvgPrice > 0) {
      setConfig(prev => ({
        ...prev,
        avgPurchasePrice: trackedCollateralData.weightedAvgPrice
      }));
    }
  }, [trackedCollateralData, useManualPrice]);

  // Auto-update node count based on tier selection
  useEffect(() => {
    if (nodes.length > 0 && nodeCountByTier[config.tier] > 0) {
      setConfig(prev => ({
        ...prev,
        nodeCount: nodeCountByTier[prev.tier] || prev.nodeCount
      }));
    }
  }, [nodeCountByTier, config.tier, nodes.length]);

  // Update daily rewards when realRewards changes
  useEffect(() => {
    if (realRewards?.byTier) {
      setConfig(prev => ({
        ...prev,
        dailyRewardPerNode: realRewards.byTier[prev.tier]?.daily || prev.dailyRewardPerNode
      }));
    }
  }, [realRewards]);

  // Update daily rewards when tier changes
  const handleTierChange = (tier: NodeTier) => {
    setConfig(prev => ({
      ...prev,
      tier,
      nodeCount: nodeCountByTier[tier] || prev.nodeCount,
      dailyRewardPerNode: realRewards?.byTier?.[tier]?.daily ||
        (tier === 'CUMULUS' ? 2 : tier === 'NIMBUS' ? 5 : 8)
    }));
  };

  // Load saved config from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('fluxnode_profit_calculator');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setConfig(prev => ({ ...prev, ...parsed }));
        // Also restore manual price mode
        if (parsed.useManualPrice !== undefined) {
          setUseManualPrice(parsed.useManualPrice);
        }
      } catch (e) {
        console.error('Error loading calculator config:', e);
      }
    }
  }, []);

  // Save config to localStorage
  useEffect(() => {
    localStorage.setItem('fluxnode_profit_calculator', JSON.stringify({ ...config, useManualPrice }));
  }, [config, useManualPrice]);

  // Effective price for calculations (simulated or real)
  const effectivePrice = simulatedPrice !== null ? simulatedPrice : fluxPrice;
  const priceVariation = fluxPrice > 0 ? ((effectivePrice - fluxPrice) / fluxPrice) * 100 : 0;

  // Calculate all metrics
  const calculations = useMemo(() => {
    const tierConfig = NODE_TIERS[config.tier];
    const totalCollateral = tierConfig.collateral * config.nodeCount;

    // Collateral costs (including exchange fees)
    const collateralBasePrice = totalCollateral * config.avgPurchasePrice;
    const exchangeFees = collateralBasePrice * (config.exchangeFeePercent / 100);
    const collateralCostAtPurchase = collateralBasePrice + exchangeFees;
    const collateralCurrentValue = totalCollateral * effectivePrice;
    const collateralPnL = collateralCurrentValue - collateralCostAtPurchase;
    const collateralPnLPercent = collateralCostAtPurchase > 0
      ? ((collateralPnL / collateralCostAtPurchase) * 100)
      : 0;

    // Hardware costs
    const totalHardwareCost = config.serverCost + config.upsCost;
    const yearlyMaintenanceCost = config.serverCost * (config.maintenancePercent / 100);
    const monthlyMaintenanceCost = yearlyMaintenanceCost / 12;

    // Hosting savings (self-hosted vs third-party)
    const thirdPartyHostingCostPerNode = config.thirdPartyHostingCosts[config.tier];
    const monthlyThirdPartyCost = thirdPartyHostingCostPerNode * config.nodeCount;
    const monthlySelfHostedCost = config.selfHostedElectricity + config.selfHostedInternet +
                                   config.coolingCost + config.fixedIpCost + monthlyMaintenanceCost;
    const monthlyHostingSavings = monthlyThirdPartyCost - monthlySelfHostedCost;
    const yearlyHostingSavings = monthlyHostingSavings * 12;

    // Server amortization (over 3 years = 36 months)
    const serverAmortizationMonths = 36;
    const monthlyServerCost = totalHardwareCost / serverAmortizationMonths;

    // Net monthly savings (hosting savings - server amortization)
    const netMonthlySavings = monthlyHostingSavings - monthlyServerCost;

    // Server payback period (including UPS)
    const serverPaybackMonths = monthlyHostingSavings > 0
      ? Math.ceil(totalHardwareCost / monthlyHostingSavings)
      : Infinity;

    // Rewards calculation (with annual decrease factor)
    const dailyRewards = config.dailyRewardPerNode * config.nodeCount;
    const monthlyRewards = dailyRewards * 30;
    const yearlyRewards = dailyRewards * 365;

    // Calculate rewards with decrease over years
    const rewardDecreaseRate = config.rewardDecreasePercent / 100;
    const year1Rewards = yearlyRewards;
    const year2Rewards = yearlyRewards * (1 - rewardDecreaseRate);
    const year3Rewards = yearlyRewards * Math.pow(1 - rewardDecreaseRate, 2);

    // Rewards value at effective price (simulated or real)
    const dailyRewardsValue = dailyRewards * effectivePrice;
    const monthlyRewardsValue = monthlyRewards * effectivePrice;
    const yearlyRewardsValue = yearlyRewards * effectivePrice;

    // Net monthly profit (rewards - self-hosted costs)
    const netMonthlyProfit = monthlyRewardsValue - monthlySelfHostedCost;
    const netYearlyProfit = netMonthlyProfit * 12;

    // ROI calculations
    const totalInvestment = totalHardwareCost + collateralCostAtPurchase;
    const yearlyROI = totalInvestment > 0
      ? ((netYearlyProfit / totalInvestment) * 100)
      : 0;

    // Break-even calculation (when does total investment pay off)
    const monthlyNetCashflow = netMonthlyProfit;
    const breakEvenMonths = monthlyNetCashflow > 0
      ? Math.ceil(totalInvestment / monthlyNetCashflow)
      : Infinity;

    // Price threshold calculation (minimum FLUX price to be profitable)
    const monthlyOperatingCosts = monthlySelfHostedCost;
    const priceThreshold = monthlyRewards > 0
      ? monthlyOperatingCosts / monthlyRewards
      : Infinity;

    // Price scenarios (12 month projections)
    const priceGrowthRates = {
      bearish: -0.30, // -30%
      neutral: 0.20,  // +20%
      bullish: 0.80   // +80%
    };

    const selectedGrowthRate = config.priceGrowthScenario === 'neutral'
      ? config.customPriceGrowth / 100
      : priceGrowthRates[config.priceGrowthScenario];

    const projectedPrice = effectivePrice * (1 + selectedGrowthRate);
    const projectedCollateralValue = totalCollateral * projectedPrice;

    // Average rewards over 3 years considering decrease
    const avgYearlyRewards = (year1Rewards + year2Rewards + year3Rewards) / 3;
    const projectedYearlyRewardsValue = avgYearlyRewards * projectedPrice;
    const projectedTotalValue = projectedCollateralValue + projectedYearlyRewardsValue;
    const projectedTotalProfit = projectedTotalValue - totalInvestment - (monthlySelfHostedCost * 12);

    // TCO (Total Cost of Ownership) over 3 years
    const tco3Years = totalHardwareCost + (monthlySelfHostedCost * 36) + collateralCostAtPurchase;

    // NPV calculation (Net Present Value) with 5% discount rate
    const discountRate = 0.05;
    let npv = -totalInvestment;
    for (let year = 1; year <= 3; year++) {
      const yearRewards = yearlyRewards * Math.pow(1 - rewardDecreaseRate, year - 1);
      const yearRewardsValue = yearRewards * effectivePrice;
      const yearCosts = monthlySelfHostedCost * 12;
      const yearCashflow = yearRewardsValue - yearCosts;
      npv += yearCashflow / Math.pow(1 + discountRate, year);
    }
    // Add back collateral value at end of year 3
    npv += collateralCurrentValue / Math.pow(1 + discountRate, 3);

    // IRR approximation (using Newton-Raphson simplified)
    const calculateIRR = () => {
      let irr = 0.1; // Start with 10%
      for (let i = 0; i < 20; i++) {
        let npvAtIrr = -totalInvestment;
        let derivativeNpv = 0;
        for (let year = 1; year <= 3; year++) {
          const yearRewards = yearlyRewards * Math.pow(1 - rewardDecreaseRate, year - 1);
          const yearRewardsValue = yearRewards * effectivePrice;
          const yearCosts = monthlySelfHostedCost * 12;
          const yearCashflow = yearRewardsValue - yearCosts;
          npvAtIrr += yearCashflow / Math.pow(1 + irr, year);
          derivativeNpv -= year * yearCashflow / Math.pow(1 + irr, year + 1);
        }
        npvAtIrr += collateralCurrentValue / Math.pow(1 + irr, 3);
        derivativeNpv -= 3 * collateralCurrentValue / Math.pow(1 + irr, 4);

        if (Math.abs(derivativeNpv) < 0.0001) break;
        irr = irr - npvAtIrr / derivativeNpv;
        if (irr < -0.99) irr = -0.99;
        if (irr > 10) irr = 10;
      }
      return irr * 100;
    };
    const irr = calculateIRR();

    return {
      // Collateral
      totalCollateral,
      collateralCostAtPurchase,
      collateralCurrentValue,
      collateralPnL,
      collateralPnLPercent,
      exchangeFees,

      // Hardware
      totalHardwareCost,
      yearlyMaintenanceCost,
      monthlyMaintenanceCost,

      // Hosting
      monthlyThirdPartyCost,
      monthlySelfHostedCost,
      monthlyHostingSavings,
      yearlyHostingSavings,
      monthlyServerCost,
      netMonthlySavings,
      serverPaybackMonths,

      // Rewards
      dailyRewards,
      monthlyRewards,
      yearlyRewards,
      dailyRewardsValue,
      monthlyRewardsValue,
      yearlyRewardsValue,
      year1Rewards,
      year2Rewards,
      year3Rewards,

      // Profit
      netMonthlyProfit,
      netYearlyProfit,

      // ROI & Financial metrics
      totalInvestment,
      yearlyROI,
      breakEvenMonths,
      priceThreshold,
      tco3Years,
      npv,
      irr,

      // Projections
      projectedPrice,
      projectedCollateralValue,
      projectedYearlyRewardsValue,
      projectedTotalValue,
      projectedTotalProfit
    };
  }, [config, effectivePrice]);

  // Export report function
  const exportReport = () => {
    const report = `
═══════════════════════════════════════════════════════════════
           RAPPORT DE RENTABILITÉ FLUXNODE
           Généré le ${new Date().toLocaleDateString('fr-FR')}
═══════════════════════════════════════════════════════════════

📊 CONFIGURATION
────────────────────────────────────────────────────────────────
Type de Node: ${config.tier}
Nombre de Nodes: ${config.nodeCount}
Prix FLUX actuel: $${effectivePrice.toFixed(4)}
Prix d'achat moyen: $${config.avgPurchasePrice}

💰 INVESTISSEMENT INITIAL
────────────────────────────────────────────────────────────────
Serveur: €${config.serverCost.toLocaleString()}
UPS: €${config.upsCost.toLocaleString()}
Collatéral: ${calculations.totalCollateral.toLocaleString()} FLUX ($${calculations.collateralCostAtPurchase.toLocaleString()})
Total: $${calculations.totalInvestment.toLocaleString()}

📈 MÉTRIQUES CLÉS
────────────────────────────────────────────────────────────────
Prix Seuil Rentabilité: $${calculations.priceThreshold.toFixed(4)}
ROI Annuel: ${calculations.yearlyROI.toFixed(1)}%
Point Mort: ${calculations.breakEvenMonths === Infinity ? '∞' : `${calculations.breakEvenMonths} mois`}
TRI (3 ans): ${calculations.irr.toFixed(1)}%
VAN (3 ans): $${calculations.npv.toLocaleString()}
TCO (3 ans): $${calculations.tco3Years.toLocaleString()}

🎁 REWARDS
────────────────────────────────────────────────────────────────
Journaliers: ${calculations.dailyRewards.toFixed(2)} FLUX ($${calculations.dailyRewardsValue.toFixed(2)})
Mensuels: ${calculations.monthlyRewards.toFixed(2)} FLUX ($${calculations.monthlyRewardsValue.toFixed(2)})
Annuels: ${calculations.yearlyRewards.toFixed(2)} FLUX ($${calculations.yearlyRewardsValue.toFixed(2)})

💸 COÛTS MENSUELS (Self-Hosted)
────────────────────────────────────────────────────────────────
Électricité: €${config.selfHostedElectricity}
Internet: €${config.selfHostedInternet}
Climatisation: €${config.coolingCost}
IP Fixe: €${config.fixedIpCost}
Maintenance: €${calculations.monthlyMaintenanceCost.toFixed(2)}
Total: €${calculations.monthlySelfHostedCost.toFixed(2)}

📊 PROFIT NET
────────────────────────────────────────────────────────────────
Mensuel: $${calculations.netMonthlyProfit.toFixed(2)}
Annuel: $${calculations.netYearlyProfit.toFixed(2)}

═══════════════════════════════════════════════════════════════
⚠️ Ce rapport est fourni à titre indicatif. Ne constitue pas
   un conseil en investissement. Les calculs sont basés sur
   les données actuelles et peuvent évoluer.
═══════════════════════════════════════════════════════════════
`;

    // Create and download the file
    const blob = new Blob([report], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rapport-fluxnode-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const formatCurrency = (value: number, currency = '€') => {
    return `${value >= 0 ? '' : '-'}${currency}${Math.abs(value).toLocaleString('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const formatFlux = (value: number) => {
    return `${value.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} FLUX`;
  };

  // Collapsible Section Header Component
  const SectionHeader = ({
    title,
    icon,
    sectionKey,
    color = 'var(--text)',
    subtitle
  }: {
    title: string;
    icon: string;
    sectionKey: keyof typeof sections;
    color?: string;
    subtitle?: string;
  }) => (
    <div
      onClick={() => toggleSection(sectionKey)}
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 16px',
        background: 'var(--border)',
        borderRadius: sections[sectionKey] ? '12px 12px 0 0' : '12px',
        cursor: 'pointer',
        userSelect: 'none',
        marginBottom: sections[sectionKey] ? '0' : '16px'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <span style={{ fontSize: '16px' }}>{icon}</span>
        <div>
          <h3 style={{ fontSize: '14px', fontWeight: 600, color, margin: 0 }}>{title}</h3>
          {subtitle && <span style={{ fontSize: '11px', color: 'var(--muted)' }}>{subtitle}</span>}
        </div>
      </div>
      <span style={{
        transition: 'transform 0.2s',
        transform: sections[sectionKey] ? 'rotate(0deg)' : 'rotate(-90deg)',
        color: 'var(--muted)'
      }}>
        <Icons.ChevronDown size={18} />
      </span>
    </div>
  );

  return (
    <div className="card" style={{ marginBottom: '30px' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: isExpanded ? '20px' : 0
        }}
      >
        <div
          style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <h2 style={{
            fontSize: '18px',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <Icons.Calculator size={22} />
            Calculateur de Rentabilité FluxNode
          </h2>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* View Mode Selector */}
          <div style={{ display: 'flex', gap: '2px', background: 'var(--border)', borderRadius: '6px', padding: '2px' }}>
            {(['essential', 'standard', 'expert'] as ViewMode[]).map(mode => (
              <button
                key={mode}
                onClick={() => {
                  setViewMode(mode);
                  // Auto-expand/collapse sections based on mode
                  if (mode === 'essential') {
                    setSections({ config: true, results: true, chart: false, comparison: false, risk: false, details: false });
                  } else if (mode === 'standard') {
                    setSections({ config: true, results: true, chart: true, comparison: false, risk: false, details: false });
                  } else {
                    setSections({ config: true, results: true, chart: true, comparison: true, risk: true, details: true });
                  }
                }}
                style={{
                  padding: '4px 10px',
                  fontSize: '11px',
                  fontWeight: 600,
                  background: viewMode === mode ? 'var(--card-bg)' : 'transparent',
                  color: viewMode === mode ? 'var(--text)' : 'var(--muted)',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {mode === 'essential' ? '📊 Essentiel' :
                 mode === 'standard' ? '📈 Standard' : '🔬 Expert'}
              </button>
            ))}
          </div>

          {/* Scenarios Button */}
          <button
            onClick={() => setShowScenariosPanel(!showScenariosPanel)}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              fontWeight: 600,
              background: showScenariosPanel ? '#10b981' : 'var(--card-bg)',
              color: showScenariosPanel ? 'white' : 'var(--muted)',
              border: `1px solid ${showScenariosPanel ? '#10b981' : 'var(--border)'}`,
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            📁 {savedScenarios.length > 0 && `(${savedScenarios.length})`}
          </button>
          {/* Export Button */}
          <button
            onClick={exportReport}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              fontWeight: 600,
              background: 'var(--card-bg)',
              color: 'var(--muted)',
              border: '1px solid var(--border)',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
            title="Exporter le rapport"
          >
            📥 Export
          </button>
          {/* Pro Mode Toggle */}
          <button
            onClick={() => setProMode(!proMode)}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              fontWeight: 600,
              background: proMode ? 'linear-gradient(135deg, #8b5cf6, #6366f1)' : 'var(--card-bg)',
              color: proMode ? 'white' : 'var(--muted)',
              border: `1px solid ${proMode ? '#8b5cf6' : 'var(--border)'}`,
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            {proMode ? '✓ PRO' : 'PRO'}
          </button>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              color: 'var(--text)'
            }}
          >
            {isExpanded ? <Icons.ChevronUp size={20} /> : <Icons.ChevronDown size={20} />}
          </button>
        </div>
      </div>

      {/* Scenarios Panel */}
      {showScenariosPanel && (
        <div style={{
          padding: '16px',
          background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(34, 197, 94, 0.1))',
          borderRadius: '12px',
          marginBottom: '20px',
          border: '1px solid rgba(16, 185, 129, 0.3)'
        }}>
          <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: '#10b981' }}>
            📁 Scénarios Sauvegardés
          </h3>

          {/* Save new scenario */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            <input
              type="text"
              placeholder="Nom du scénario..."
              value={scenarioName}
              onChange={(e) => setScenarioName(e.target.value)}
              style={{
                flex: 1,
                padding: '8px 12px',
                fontSize: '13px',
                background: 'var(--card-bg)',
                color: 'var(--text)',
                border: '1px solid var(--border)',
                borderRadius: '6px'
              }}
            />
            <button
              onClick={saveScenario}
              style={{
                padding: '8px 16px',
                fontSize: '12px',
                fontWeight: 600,
                background: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              💾 Sauvegarder
            </button>
          </div>

          {/* Saved scenarios list */}
          {savedScenarios.length > 0 ? (
            <div style={{ display: 'grid', gap: '8px' }}>
              {savedScenarios.map(scenario => (
                <div
                  key={scenario.id}
                  style={{
                    padding: '12px',
                    background: 'var(--card-bg)',
                    borderRadius: '8px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '13px' }}>{scenario.name}</div>
                    <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
                      {scenario.config.tier} × {scenario.config.nodeCount} •
                      {new Date(scenario.date).toLocaleDateString('fr-FR')}
                      {scenario.proMode && <span style={{ color: '#8b5cf6', marginLeft: '8px' }}>PRO</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      onClick={() => loadScenario(scenario)}
                      style={{
                        padding: '6px 12px',
                        fontSize: '11px',
                        fontWeight: 600,
                        background: '#6366f1',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Charger
                    </button>
                    <button
                      onClick={() => deleteScenario(scenario.id)}
                      style={{
                        padding: '6px 10px',
                        fontSize: '11px',
                        fontWeight: 600,
                        background: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{
              textAlign: 'center',
              padding: '20px',
              color: 'var(--muted)',
              fontSize: '13px'
            }}>
              Aucun scénario sauvegardé. Configurez vos paramètres et cliquez sur "Sauvegarder".
            </div>
          )}
        </div>
      )}

      {isExpanded && (
        <>
          {/* Pro Mode Panel */}
          {proMode && (
            <div style={{
              padding: '16px',
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(99, 102, 241, 0.1))',
              borderRadius: '12px',
              marginBottom: '20px',
              border: '1px solid rgba(139, 92, 246, 0.3)'
            }}>
              <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: '#8b5cf6' }}>
                ⚙️ Configuration Professionnelle
              </h3>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                {/* Multi-server */}
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--muted)', display: 'block', marginBottom: '4px' }}>
                    Nombre de serveurs
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={proConfig.serverCount}
                    onChange={(e) => setProConfig(prev => ({ ...prev, serverCount: parseInt(e.target.value) || 1 }))}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: '14px',
                      background: 'var(--card-bg)',
                      color: 'var(--text)',
                      border: '1px solid var(--border)',
                      borderRadius: '6px'
                    }}
                  />
                </div>

                {/* Power consumption */}
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--muted)', display: 'block', marginBottom: '4px' }}>
                    Consommation (W/serveur)
                  </label>
                  <input
                    type="number"
                    min="50"
                    max="2000"
                    value={proConfig.wattage}
                    onChange={(e) => setProConfig(prev => ({ ...prev, wattage: parseInt(e.target.value) || 150 }))}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: '14px',
                      background: 'var(--card-bg)',
                      color: 'var(--text)',
                      border: '1px solid var(--border)',
                      borderRadius: '6px'
                    }}
                  />
                </div>

                {/* Electricity price */}
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--muted)', display: 'block', marginBottom: '4px' }}>
                    Prix électricité (€/kWh)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={proConfig.electricityPriceKwh}
                    onChange={(e) => setProConfig(prev => ({ ...prev, electricityPriceKwh: parseFloat(e.target.value) || 0.25 }))}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: '14px',
                      background: 'var(--card-bg)',
                      color: 'var(--text)',
                      border: '1px solid var(--border)',
                      borderRadius: '6px'
                    }}
                  />
                </div>

                {/* Insurance */}
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--muted)', display: 'block', marginBottom: '4px' }}>
                    Assurance (€/mois)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={proConfig.insuranceCost}
                    onChange={(e) => setProConfig(prev => ({ ...prev, insuranceCost: parseFloat(e.target.value) || 0 }))}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: '14px',
                      background: 'var(--card-bg)',
                      color: 'var(--text)',
                      border: '1px solid var(--border)',
                      borderRadius: '6px'
                    }}
                  />
                </div>

                {/* VAT recovery */}
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--muted)', display: 'block', marginBottom: '4px' }}>
                    TVA Récupérable
                  </label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button
                      onClick={() => setProConfig(prev => ({ ...prev, vatRecoverable: !prev.vatRecoverable }))}
                      style={{
                        padding: '8px 16px',
                        fontSize: '12px',
                        fontWeight: 600,
                        background: proConfig.vatRecoverable ? '#10b981' : 'var(--card-bg)',
                        color: proConfig.vatRecoverable ? 'white' : 'var(--text)',
                        border: `1px solid ${proConfig.vatRecoverable ? '#10b981' : 'var(--border)'}`,
                        borderRadius: '6px',
                        cursor: 'pointer'
                      }}
                    >
                      {proConfig.vatRecoverable ? '✓ Oui' : 'Non'}
                    </button>
                    {proConfig.vatRecoverable && (
                      <input
                        type="number"
                        min="0"
                        max="30"
                        value={proConfig.vatRate}
                        onChange={(e) => setProConfig(prev => ({ ...prev, vatRate: parseFloat(e.target.value) || 20 }))}
                        style={{
                          width: '70px',
                          padding: '8px',
                          fontSize: '12px',
                          background: 'var(--card-bg)',
                          color: 'var(--text)',
                          border: '1px solid var(--border)',
                          borderRadius: '6px'
                        }}
                        placeholder="%"
                      />
                    )}
                  </div>
                </div>

                {/* Depreciation type */}
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--muted)', display: 'block', marginBottom: '4px' }}>
                    Type d'amortissement
                  </label>
                  <select
                    aria-label="Type d'amortissement"
                    value={proConfig.depreciationType}
                    onChange={(e) => setProConfig(prev => ({ ...prev, depreciationType: e.target.value as 'linear' | 'degressive' }))}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: '14px',
                      background: 'var(--card-bg)',
                      color: 'var(--text)',
                      border: '1px solid var(--border)',
                      borderRadius: '6px'
                    }}
                  >
                    <option value="linear">Linéaire (3 ans)</option>
                    <option value="degressive">Dégressif (25%/an)</option>
                  </select>
                </div>
              </div>

              {/* Calculated electricity cost */}
              <div style={{
                marginTop: '16px',
                padding: '12px',
                background: 'var(--card-bg)',
                borderRadius: '8px',
                fontSize: '12px'
              }}>
                <div style={{ fontWeight: 600, marginBottom: '8px', color: '#8b5cf6' }}>
                  📊 Calculs Pro
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '8px', color: 'var(--muted)' }}>
                  <div>
                    Électricité calculée: <span style={{ fontWeight: 600, color: 'var(--text)' }}>
                      €{((proConfig.wattage * proConfig.serverCount * 24 * 30) / 1000 * proConfig.electricityPriceKwh).toFixed(2)}/mois
                    </span>
                  </div>
                  <div>
                    Coût total serveurs: <span style={{ fontWeight: 600, color: 'var(--text)' }}>
                      €{(config.serverCost * proConfig.serverCount).toLocaleString()}
                    </span>
                  </div>
                  {proConfig.vatRecoverable && (
                    <div>
                      TVA récupérable: <span style={{ fontWeight: 600, color: '#10b981' }}>
                        €{((config.serverCost * proConfig.serverCount) * proConfig.vatRate / 100).toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div>
                    Amortissement {proConfig.depreciationType === 'linear' ? 'linéaire' : 'dégressif'}: <span style={{ fontWeight: 600, color: 'var(--text)' }}>
                      €{proConfig.depreciationType === 'linear'
                        ? ((config.serverCost * proConfig.serverCount) / 36).toFixed(2)
                        : ((config.serverCost * proConfig.serverCount) * 0.25 / 12).toFixed(2)
                      }/mois
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Configuration Form */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '20px',
            marginBottom: '24px'
          }}>
            {/* Node Configuration */}
            <div style={{
              padding: '16px',
              background: 'rgba(59, 130, 246, 0.08)',
              borderRadius: '12px',
              border: '1px solid rgba(59, 130, 246, 0.15)'
            }}>
              <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: '#3b82f6' }}>
                📊 Configuration Nodes
              </h3>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ fontSize: '12px', color: 'var(--muted)', display: 'block', marginBottom: '4px' }}>
                  Type de Node
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {(Object.keys(NODE_TIERS) as NodeTier[]).map(tier => (
                    <button
                      key={tier}
                      onClick={() => handleTierChange(tier)}
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        fontSize: '12px',
                        fontWeight: 600,
                        background: config.tier === tier ? NODE_TIERS[tier].color : 'var(--card-bg)',
                        color: config.tier === tier ? 'white' : 'var(--text)',
                        border: `1px solid ${config.tier === tier ? NODE_TIERS[tier].color : 'var(--border)'}`,
                        borderRadius: '6px',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      {tier}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ fontSize: '12px', color: 'var(--muted)', display: 'block', marginBottom: '4px' }}>
                  Nombre de Nodes
                </label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={config.nodeCount}
                  onChange={(e) => setConfig(prev => ({ ...prev, nodeCount: parseInt(e.target.value) || 1 }))}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: '14px',
                    background: 'var(--card-bg)',
                    color: 'var(--text)',
                    border: '1px solid var(--border)',
                    borderRadius: '6px'
                  }}
                />
              </div>

              <div>
                <label style={{ fontSize: '12px', color: 'var(--muted)', display: 'block', marginBottom: '4px' }}>
                  Rewards journaliers par node (FLUX)
                  {realRewards?.byTier?.[config.tier]?.daily && (
                    <span style={{ color: '#10b981', marginLeft: '8px', fontSize: '10px' }}>
                      Live: {realRewards.byTier[config.tier].daily.toFixed(2)}/jour
                    </span>
                  )}
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={config.dailyRewardPerNode}
                    onChange={(e) => setConfig(prev => ({ ...prev, dailyRewardPerNode: parseFloat(e.target.value) || 0 }))}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      fontSize: '14px',
                      background: 'var(--card-bg)',
                      color: 'var(--text)',
                      border: '1px solid var(--border)',
                      borderRadius: '6px'
                    }}
                  />
                  {realRewards?.byTier?.[config.tier]?.daily && (
                    <button
                      onClick={() => setConfig(prev => ({
                        ...prev,
                        dailyRewardPerNode: realRewards.byTier[config.tier].daily
                      }))}
                      style={{
                        padding: '8px 12px',
                        fontSize: '11px',
                        fontWeight: 600,
                        background: config.dailyRewardPerNode === realRewards.byTier[config.tier].daily
                          ? '#10b981'
                          : 'var(--card-bg)',
                        color: config.dailyRewardPerNode === realRewards.byTier[config.tier].daily
                          ? 'white'
                          : '#10b981',
                        border: '1px solid #10b981',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap'
                      }}
                      title="Utiliser les rewards réels du réseau"
                    >
                      {config.dailyRewardPerNode === realRewards.byTier[config.tier].daily ? '✓ Live' : 'Sync Live'}
                    </button>
                  )}
                </div>
                {realRewards?.byTier && (
                  <div style={{
                    marginTop: '8px',
                    padding: '8px',
                    background: 'rgba(16, 185, 129, 0.1)',
                    borderRadius: '6px',
                    fontSize: '10px',
                    color: 'var(--muted)'
                  }}>
                    <div style={{ fontWeight: 600, color: '#10b981', marginBottom: '4px' }}>
                      APY Réseau (temps réel)
                    </div>
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <span style={{ color: NODE_TIERS.CUMULUS.color }}>
                        CUMULUS: {realRewards.byTier.CUMULUS.daily.toFixed(2)}/j
                      </span>
                      <span style={{ color: NODE_TIERS.NIMBUS.color }}>
                        NIMBUS: {realRewards.byTier.NIMBUS.daily.toFixed(2)}/j
                      </span>
                      <span style={{ color: NODE_TIERS.STRATUS.color }}>
                        STRATUS: {realRewards.byTier.STRATUS.daily.toFixed(2)}/j
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Hardware & Hosting Costs */}
            <div style={{
              padding: '16px',
              background: 'rgba(16, 185, 129, 0.08)',
              borderRadius: '12px',
              border: '1px solid rgba(16, 185, 129, 0.15)'
            }}>
              <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: '#10b981' }}>
                🖥️ Coûts Matériel & Hébergement
              </h3>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--muted)', display: 'block', marginBottom: '4px' }}>
                    Coût serveur (€)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={config.serverCost}
                    onChange={(e) => setConfig(prev => ({ ...prev, serverCost: parseFloat(e.target.value) || 0 }))}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: '14px',
                      background: 'var(--card-bg)',
                      color: 'var(--text)',
                      border: '1px solid var(--border)',
                      borderRadius: '6px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--muted)', display: 'block', marginBottom: '4px' }}>
                    UPS/Onduleur (€)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={config.upsCost}
                    onChange={(e) => setConfig(prev => ({ ...prev, upsCost: parseFloat(e.target.value) || 0 }))}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: '14px',
                      background: 'var(--card-bg)',
                      color: 'var(--text)',
                      border: '1px solid var(--border)',
                      borderRadius: '6px'
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ fontSize: '12px', color: 'var(--muted)', display: 'block', marginBottom: '4px' }}>
                  Maintenance annuelle (% du serveur)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={config.maintenancePercent}
                  onChange={(e) => setConfig(prev => ({ ...prev, maintenancePercent: parseFloat(e.target.value) || 0 }))}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: '14px',
                    background: 'var(--card-bg)',
                    color: 'var(--text)',
                    border: '1px solid var(--border)',
                    borderRadius: '6px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ fontSize: '12px', color: 'var(--muted)', display: 'block', marginBottom: '8px' }}>
                  Hébergement tiers (€/mois/node)
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                  {(Object.keys(NODE_TIERS) as NodeTier[]).map(tier => (
                    <div key={tier}>
                      <label style={{
                        fontSize: '10px',
                        color: config.tier === tier ? NODE_TIERS[tier].color : 'var(--muted)',
                        display: 'block',
                        marginBottom: '4px',
                        fontWeight: config.tier === tier ? 600 : 400
                      }}>
                        {tier}
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={config.thirdPartyHostingCosts[tier]}
                        onChange={(e) => setConfig(prev => ({
                          ...prev,
                          thirdPartyHostingCosts: {
                            ...prev.thirdPartyHostingCosts,
                            [tier]: parseFloat(e.target.value) || 0
                          }
                        }))}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          fontSize: '13px',
                          background: config.tier === tier ? `${NODE_TIERS[tier].color}15` : 'var(--card-bg)',
                          color: 'var(--text)',
                          border: `2px solid ${config.tier === tier ? NODE_TIERS[tier].color : 'var(--border)'}`,
                          borderRadius: '6px'
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--muted)', display: 'block', marginBottom: '4px' }}>
                    Électricité (€/mois)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={config.selfHostedElectricity}
                    onChange={(e) => setConfig(prev => ({ ...prev, selfHostedElectricity: parseFloat(e.target.value) || 0 }))}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: '14px',
                      background: 'var(--card-bg)',
                      color: 'var(--text)',
                      border: '1px solid var(--border)',
                      borderRadius: '6px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--muted)', display: 'block', marginBottom: '4px' }}>
                    Internet (€/mois)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={config.selfHostedInternet}
                    onChange={(e) => setConfig(prev => ({ ...prev, selfHostedInternet: parseFloat(e.target.value) || 0 }))}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: '14px',
                      background: 'var(--card-bg)',
                      color: 'var(--text)',
                      border: '1px solid var(--border)',
                      borderRadius: '6px'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--muted)', display: 'block', marginBottom: '4px' }}>
                    Climatisation (€/mois)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={config.coolingCost}
                    onChange={(e) => setConfig(prev => ({ ...prev, coolingCost: parseFloat(e.target.value) || 0 }))}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: '14px',
                      background: 'var(--card-bg)',
                      color: 'var(--text)',
                      border: '1px solid var(--border)',
                      borderRadius: '6px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--muted)', display: 'block', marginBottom: '4px' }}>
                    IP fixe (€/mois)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={config.fixedIpCost}
                    onChange={(e) => setConfig(prev => ({ ...prev, fixedIpCost: parseFloat(e.target.value) || 0 }))}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: '14px',
                      background: 'var(--card-bg)',
                      color: 'var(--text)',
                      border: '1px solid var(--border)',
                      borderRadius: '6px'
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Collateral & Price */}
            <div style={{
              padding: '16px',
              background: 'rgba(139, 92, 246, 0.08)',
              borderRadius: '12px',
              border: '1px solid rgba(139, 92, 246, 0.15)'
            }}>
              <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: '#8b5cf6' }}>
                💰 Collatéral & Prix
              </h3>

              {/* Data source indicator */}
              {trackedCollateralData && trackedCollateralData.trackedNodesCount > 0 && (
                <div style={{
                  padding: '8px 12px',
                  background: 'rgba(16, 185, 129, 0.15)',
                  borderRadius: '6px',
                  marginBottom: '12px',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '8px'
                }}>
                  <span style={{ color: '#10b981' }}>
                    ✓ {trackedCollateralData.trackedNodesCount} node(s) avec prix d'achat renseigné
                  </span>
                  <button
                    onClick={() => setUseManualPrice(!useManualPrice)}
                    style={{
                      padding: '4px 8px',
                      fontSize: '11px',
                      background: useManualPrice ? '#6366f1' : 'transparent',
                      color: useManualPrice ? 'white' : 'var(--muted)',
                      border: '1px solid var(--border)',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    {useManualPrice ? 'Mode manuel' : 'Mode auto'}
                  </button>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '8px', marginBottom: '12px' }}>
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--muted)', display: 'block', marginBottom: '4px' }}>
                    Prix moyen d'achat FLUX ($)
                    {!useManualPrice && trackedCollateralData && trackedCollateralData.weightedAvgPrice > 0 && (
                      <span style={{ color: '#10b981', marginLeft: '8px' }}>
                        (auto)
                      </span>
                    )}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={config.avgPurchasePrice}
                    onChange={(e) => {
                      setUseManualPrice(true);
                      setConfig(prev => ({ ...prev, avgPurchasePrice: parseFloat(e.target.value) || 0 }));
                    }}
                    disabled={!useManualPrice && trackedCollateralData && trackedCollateralData.weightedAvgPrice > 0}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: '14px',
                      background: (!useManualPrice && trackedCollateralData?.weightedAvgPrice) ? 'rgba(16, 185, 129, 0.1)' : 'var(--card-bg)',
                      color: 'var(--text)',
                      border: `1px solid ${(!useManualPrice && trackedCollateralData?.weightedAvgPrice) ? '#10b981' : 'var(--border)'}`,
                      borderRadius: '6px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--muted)', display: 'block', marginBottom: '4px' }}>
                    Frais exchange (%)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="10"
                    value={config.exchangeFeePercent}
                    onChange={(e) => setConfig(prev => ({ ...prev, exchangeFeePercent: parseFloat(e.target.value) || 0 }))}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: '14px',
                      background: 'var(--card-bg)',
                      color: 'var(--text)',
                      border: '1px solid var(--border)',
                      borderRadius: '6px'
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ fontSize: '12px', color: 'var(--muted)', display: 'block', marginBottom: '4px' }}>
                  Décroissance rewards annuelle (%) - dilution réseau
                </label>
                <input
                  type="number"
                  step="1"
                  min="0"
                  max="50"
                  value={config.rewardDecreasePercent}
                  onChange={(e) => setConfig(prev => ({ ...prev, rewardDecreasePercent: parseFloat(e.target.value) || 0 }))}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: '14px',
                    background: 'var(--card-bg)',
                    color: 'var(--text)',
                    border: '1px solid var(--border)',
                    borderRadius: '6px'
                  }}
                />
                <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '4px' }}>
                  Prend en compte l'augmentation du nombre de nodes sur le réseau
                </div>
              </div>

              {/* Price Simulation Slider */}
              <div style={{
                padding: '16px',
                background: 'var(--card-bg)',
                borderRadius: '8px',
                marginBottom: '12px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                    Prix FLUX pour simulation
                  </div>
                  {simulatedPrice !== null && (
                    <button
                      onClick={() => setSimulatedPrice(null)}
                      style={{
                        padding: '4px 8px',
                        fontSize: '10px',
                        background: 'rgba(239, 68, 68, 0.1)',
                        color: '#ef4444',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Reset
                    </button>
                  )}
                </div>

                {/* Price display */}
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', marginBottom: '12px' }}>
                  <div style={{
                    fontSize: '28px',
                    fontWeight: 700,
                    color: simulatedPrice !== null
                      ? (priceVariation >= 0 ? '#10b981' : '#ef4444')
                      : '#10b981'
                  }}>
                    ${effectivePrice.toFixed(4)}
                  </div>
                  {simulatedPrice !== null && (
                    <div style={{
                      fontSize: '14px',
                      fontWeight: 600,
                      color: priceVariation >= 0 ? '#10b981' : '#ef4444',
                      background: priceVariation >= 0 ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                      padding: '4px 8px',
                      borderRadius: '4px'
                    }}>
                      {priceVariation >= 0 ? '+' : ''}{priceVariation.toFixed(1)}%
                    </div>
                  )}
                  {simulatedPrice === null && (
                    <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
                      (temps réel)
                    </div>
                  )}
                </div>

                {/* Slider */}
                <div style={{ marginBottom: '8px' }}>
                  <input
                    type="range"
                    min={Math.max(0.01, fluxPrice * 0.2)}
                    max={fluxPrice * 3}
                    step={fluxPrice * 0.01}
                    value={effectivePrice}
                    onChange={(e) => setSimulatedPrice(parseFloat(e.target.value))}
                    style={{
                      width: '100%',
                      height: '8px',
                      borderRadius: '4px',
                      background: `linear-gradient(to right,
                        #ef4444 0%,
                        #f59e0b ${33}%,
                        #10b981 ${50}%,
                        #3b82f6 ${75}%,
                        #8b5cf6 100%)`,
                      appearance: 'none',
                      cursor: 'pointer'
                    }}
                  />
                </div>

                {/* Scale markers */}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: 'var(--muted)' }}>
                  <span>-80%</span>
                  <span style={{ color: simulatedPrice === null ? '#10b981' : 'var(--muted)' }}>
                    Actuel: ${fluxPrice.toFixed(4)}
                  </span>
                  <span>+200%</span>
                </div>
              </div>

              <div>
                <label style={{ fontSize: '12px', color: 'var(--muted)', display: 'block', marginBottom: '4px' }}>
                  Scénario évolution prix (12 mois)
                </label>
                <select
                  aria-label="Scénario évolution prix (12 mois)"
                  value={config.priceGrowthScenario}
                  onChange={(e) => setConfig(prev => ({
                    ...prev,
                    priceGrowthScenario: e.target.value as 'bearish' | 'neutral' | 'bullish'
                  }))}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: '14px',
                    background: 'var(--card-bg)',
                    color: 'var(--text)',
                    border: '1px solid var(--border)',
                    borderRadius: '6px'
                  }}
                >
                  <option value="bearish">🐻 Baissier (-30%)</option>
                  <option value="neutral">📊 Neutre (+20%)</option>
                  <option value="bullish">🐂 Haussier (+80%)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Results Section */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '12px',
            marginBottom: '24px'
          }}>
            {/* Prix Seuil */}
            <div style={{
              padding: '14px',
              background: calculations.priceThreshold < effectivePrice
                ? 'linear-gradient(135deg, #10b981, #059669)'
                : 'linear-gradient(135deg, #ef4444, #dc2626)',
              borderRadius: '12px',
              color: 'white'
            }}>
              <div style={{ fontSize: '11px', opacity: 0.9, marginBottom: '4px' }}>
                🎚️ Prix Seuil Rentabilité
              </div>
              <div style={{ fontSize: '22px', fontWeight: 700 }}>
                ${calculations.priceThreshold.toFixed(4)}
              </div>
              <div style={{ fontSize: '10px', opacity: 0.8, marginTop: '4px' }}>
                {calculations.priceThreshold < effectivePrice
                  ? `✓ Marge: +${(((effectivePrice - calculations.priceThreshold) / calculations.priceThreshold) * 100).toFixed(0)}%`
                  : `⚠️ Déficit: ${(((effectivePrice - calculations.priceThreshold) / calculations.priceThreshold) * 100).toFixed(0)}%`}
              </div>
            </div>

            {/* Économies Hébergement */}
            <div style={{
              padding: '14px',
              background: calculations.monthlyHostingSavings >= 0
                ? 'linear-gradient(135deg, #10b981, #059669)'
                : 'linear-gradient(135deg, #ef4444, #dc2626)',
              borderRadius: '12px',
              color: 'white'
            }}>
              <div style={{ fontSize: '11px', opacity: 0.9, marginBottom: '4px' }}>
                💸 Économies Héberg./mois
              </div>
              <div style={{ fontSize: '22px', fontWeight: 700 }}>
                {formatCurrency(calculations.monthlyHostingSavings)}
              </div>
              <div style={{ fontSize: '10px', opacity: 0.8, marginTop: '4px' }}>
                {formatCurrency(calculations.yearlyHostingSavings)}/an
              </div>
            </div>

            {/* Server Payback */}
            <div style={{
              padding: '14px',
              background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
              borderRadius: '12px',
              color: 'white'
            }}>
              <div style={{ fontSize: '11px', opacity: 0.9, marginBottom: '4px' }}>
                ⏱️ Amortissement Serveur
              </div>
              <div style={{ fontSize: '22px', fontWeight: 700 }}>
                {calculations.serverPaybackMonths === Infinity
                  ? '∞'
                  : `${calculations.serverPaybackMonths} mois`}
              </div>
              <div style={{ fontSize: '10px', opacity: 0.8, marginTop: '4px' }}>
                {calculations.serverPaybackMonths <= 12 ? '✓ Rentable < 1 an' :
                 calculations.serverPaybackMonths <= 24 ? '✓ Rentable < 2 ans' :
                 'Long terme'}
              </div>
            </div>

            {/* Monthly Rewards */}
            <div style={{
              padding: '14px',
              background: 'linear-gradient(135deg, #f59e0b, #d97706)',
              borderRadius: '12px',
              color: 'white'
            }}>
              <div style={{ fontSize: '11px', opacity: 0.9, marginBottom: '4px' }}>
                🎁 Rewards Mensuels
              </div>
              <div style={{ fontSize: '22px', fontWeight: 700 }}>
                {formatFlux(calculations.monthlyRewards)}
              </div>
              <div style={{ fontSize: '10px', opacity: 0.8, marginTop: '4px' }}>
                ≈ {formatCurrency(calculations.monthlyRewardsValue, '$')}
              </div>
            </div>

            {/* Net Monthly Profit */}
            <div style={{
              padding: '14px',
              background: calculations.netMonthlyProfit >= 0
                ? 'linear-gradient(135deg, #22c55e, #16a34a)'
                : 'linear-gradient(135deg, #ef4444, #dc2626)',
              borderRadius: '12px',
              color: 'white'
            }}>
              <div style={{ fontSize: '11px', opacity: 0.9, marginBottom: '4px' }}>
                📈 Profit Net Mensuel
              </div>
              <div style={{ fontSize: '22px', fontWeight: 700 }}>
                {formatCurrency(calculations.netMonthlyProfit, '$')}
              </div>
              <div style={{ fontSize: '10px', opacity: 0.8, marginTop: '4px' }}>
                Après coûts self-hosted
              </div>
            </div>

            {/* Yearly ROI */}
            <div style={{
              padding: '14px',
              background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
              borderRadius: '12px',
              color: 'white'
            }}>
              <div style={{ fontSize: '11px', opacity: 0.9, marginBottom: '4px' }}>
                📊 ROI Annuel
              </div>
              <div style={{ fontSize: '22px', fontWeight: 700 }}>
                {calculations.yearlyROI.toFixed(1)}%
              </div>
              <div style={{ fontSize: '10px', opacity: 0.8, marginTop: '4px' }}>
                Sur investissement total
              </div>
            </div>

            {/* Break Even */}
            <div style={{
              padding: '14px',
              background: 'linear-gradient(135deg, #ec4899, #db2777)',
              borderRadius: '12px',
              color: 'white'
            }}>
              <div style={{ fontSize: '11px', opacity: 0.9, marginBottom: '4px' }}>
                🎯 Point Mort
              </div>
              <div style={{ fontSize: '22px', fontWeight: 700 }}>
                {calculations.breakEvenMonths === Infinity
                  ? '∞'
                  : `${calculations.breakEvenMonths} mois`}
              </div>
              <div style={{ fontSize: '10px', opacity: 0.8, marginTop: '4px' }}>
                Remboursement total invest.
              </div>
            </div>

            {/* TRI (IRR) */}
            <div style={{
              padding: '14px',
              background: calculations.irr >= 0
                ? 'linear-gradient(135deg, #14b8a6, #0d9488)'
                : 'linear-gradient(135deg, #ef4444, #dc2626)',
              borderRadius: '12px',
              color: 'white'
            }}>
              <div style={{ fontSize: '11px', opacity: 0.9, marginBottom: '4px' }}>
                📐 TRI (3 ans)
              </div>
              <div style={{ fontSize: '22px', fontWeight: 700 }}>
                {isFinite(calculations.irr) ? `${calculations.irr.toFixed(1)}%` : 'N/A'}
              </div>
              <div style={{ fontSize: '10px', opacity: 0.8, marginTop: '4px' }}>
                Taux Rendement Interne
              </div>
            </div>

            {/* VAN (NPV) */}
            <div style={{
              padding: '14px',
              background: calculations.npv >= 0
                ? 'linear-gradient(135deg, #0ea5e9, #0284c7)'
                : 'linear-gradient(135deg, #ef4444, #dc2626)',
              borderRadius: '12px',
              color: 'white'
            }}>
              <div style={{ fontSize: '11px', opacity: 0.9, marginBottom: '4px' }}>
                💎 VAN (3 ans)
              </div>
              <div style={{ fontSize: '22px', fontWeight: 700 }}>
                {formatCurrency(calculations.npv, '$')}
              </div>
              <div style={{ fontSize: '10px', opacity: 0.8, marginTop: '4px' }}>
                Valeur Actuelle Nette
              </div>
            </div>

            {/* TCO */}
            <div style={{
              padding: '14px',
              background: 'linear-gradient(135deg, #64748b, #475569)',
              borderRadius: '12px',
              color: 'white'
            }}>
              <div style={{ fontSize: '11px', opacity: 0.9, marginBottom: '4px' }}>
                🧮 TCO (3 ans)
              </div>
              <div style={{ fontSize: '22px', fontWeight: 700 }}>
                {formatCurrency(calculations.tco3Years, '$')}
              </div>
              <div style={{ fontSize: '10px', opacity: 0.8, marginTop: '4px' }}>
                Coût Total Possession
              </div>
            </div>
          </div>

          {/* Breakeven Chart */}
          <div style={{
            background: 'rgba(20, 184, 166, 0.08)',
            borderRadius: '12px',
            marginBottom: '24px',
            overflow: 'hidden',
            border: '1px solid rgba(20, 184, 166, 0.15)'
          }}>
            <div
              onClick={() => toggleSection('chart')}
              style={{
                padding: '16px 20px',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                userSelect: 'none'
              }}
            >
              <h3 style={{ fontSize: '16px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px', margin: 0, color: '#14b8a6' }}>
                📈 Courbe de Rentabilité (36 mois)
              </h3>
              <span style={{
                transition: 'transform 0.2s',
                transform: sections.chart ? 'rotate(0deg)' : 'rotate(-90deg)',
                color: 'var(--muted)'
              }}>
                <Icons.ChevronDown size={18} />
              </span>
            </div>
            {sections.chart && (
            <div style={{ padding: '0 20px 20px' }}>
            <div style={{ position: 'relative', height: '200px', marginBottom: '8px' }}>
              {/* Y-axis labels */}
              <div style={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 20,
                width: '60px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                fontSize: '10px',
                color: 'var(--muted)',
                textAlign: 'right',
                paddingRight: '8px'
              }}>
                <span>{formatCurrency(Math.max(calculations.netMonthlyProfit * 36, calculations.totalInvestment) * 1.1, '$')}</span>
                <span>{formatCurrency(0, '$')}</span>
                <span>{formatCurrency(-calculations.totalInvestment, '$')}</span>
              </div>

              {/* Chart area */}
              <div style={{
                position: 'absolute',
                left: '65px',
                right: '10px',
                top: 0,
                bottom: 20,
                background: 'var(--card-bg)',
                borderRadius: '8px',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'flex-end',
                gap: '2px',
                padding: '10px'
              }}>
                {/* Zero line */}
                <div style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: `${(Math.max(calculations.netMonthlyProfit * 36, calculations.totalInvestment) * 1.1) / (Math.max(calculations.netMonthlyProfit * 36, calculations.totalInvestment) * 1.1 + calculations.totalInvestment) * 100}%`,
                  height: '2px',
                  background: 'var(--muted)',
                  opacity: 0.5,
                  zIndex: 1
                }} />

                {/* Bars */}
                {Array.from({ length: 36 }, (_, month) => {
                  const rewardDecreaseRate = config.rewardDecreasePercent / 100;
                  const yearIndex = Math.floor(month / 12);
                  const yearlyMultiplier = Math.pow(1 - rewardDecreaseRate, yearIndex);
                  const monthlyRewardsValue = calculations.monthlyRewards * effectivePrice * yearlyMultiplier;
                  const cumulativeProfit = -calculations.totalInvestment + (monthlyRewardsValue - calculations.monthlySelfHostedCost) * (month + 1);

                  const maxValue = Math.max(calculations.netMonthlyProfit * 36, calculations.totalInvestment) * 1.1;
                  const minValue = -calculations.totalInvestment;
                  const range = maxValue - minValue;
                  const heightPercent = Math.abs(cumulativeProfit - minValue) / range * 100;

                  const isBreakeven = month + 1 === calculations.breakEvenMonths;

                  return (
                    <div
                      key={month}
                      style={{
                        flex: 1,
                        height: `${heightPercent}%`,
                        background: cumulativeProfit >= 0
                          ? isBreakeven ? '#22c55e' : 'linear-gradient(180deg, #10b981, #059669)'
                          : 'linear-gradient(180deg, #ef4444, #dc2626)',
                        borderRadius: '2px 2px 0 0',
                        position: 'relative',
                        minWidth: '4px',
                        transition: 'all 0.2s',
                        cursor: 'pointer'
                      }}
                      title={`Mois ${month + 1}: ${formatCurrency(cumulativeProfit, '$')}`}
                    >
                      {isBreakeven && (
                        <div style={{
                          position: 'absolute',
                          top: '-20px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          fontSize: '10px',
                          fontWeight: 700,
                          color: '#22c55e',
                          whiteSpace: 'nowrap'
                        }}>
                          ✓ BE
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* X-axis labels */}
              <div style={{
                position: 'absolute',
                left: '65px',
                right: '10px',
                bottom: 0,
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '10px',
                color: 'var(--muted)'
              }}>
                <span>M1</span>
                <span>M12</span>
                <span>M24</span>
                <span>M36</span>
              </div>
            </div>

            {/* Legend */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '20px',
              fontSize: '11px',
              color: 'var(--muted)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '12px', height: '12px', background: 'linear-gradient(180deg, #ef4444, #dc2626)', borderRadius: '2px' }} />
                <span>Période déficitaire</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '12px', height: '12px', background: 'linear-gradient(180deg, #10b981, #059669)', borderRadius: '2px' }} />
                <span>Période rentable</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '12px', height: '12px', background: '#22c55e', borderRadius: '2px' }} />
                <span>Point mort</span>
              </div>
            </div>
            </div>
            )}
          </div>

          {/* Tier Comparison Section */}
          <div style={{
            background: 'rgba(99, 102, 241, 0.08)',
            borderRadius: '12px',
            marginBottom: '24px',
            overflow: 'hidden',
            border: '1px solid rgba(99, 102, 241, 0.15)'
          }}>
            <div
              onClick={() => toggleSection('comparison')}
              style={{
                padding: '16px 20px',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                userSelect: 'none'
              }}
            >
              <h3 style={{ fontSize: '16px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px', margin: 0, color: '#6366f1' }}>
                ⚖️ Comparaison des Tiers
              </h3>
              <span style={{
                transition: 'transform 0.2s',
                transform: sections.comparison ? 'rotate(0deg)' : 'rotate(-90deg)',
                color: 'var(--muted)'
              }}>
                <Icons.ChevronDown size={18} />
              </span>
            </div>
            {sections.comparison && (
            <div style={{ padding: '0 20px 20px' }}>
            <p style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '16px' }}>
              Comparaison basée sur un investissement en collatéral de {formatFlux(calculations.totalCollateral)} ({formatCurrency(calculations.collateralCostAtPurchase, '$')})
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
              {(Object.keys(NODE_TIERS) as NodeTier[]).map(tier => {
                const tierConfig = NODE_TIERS[tier];
                const equivalentNodes = Math.floor(calculations.totalCollateral / tierConfig.collateral);
                if (equivalentNodes === 0) return null;

                const tierCollateral = tierConfig.collateral * equivalentNodes;
                const tierHostingCost = config.thirdPartyHostingCosts[tier] * equivalentNodes;
                const tierDailyReward = realRewards?.byTier?.[tier]?.daily || (tier === 'CUMULUS' ? 2 : tier === 'NIMBUS' ? 5 : 8);
                const tierMonthlyRewards = tierDailyReward * equivalentNodes * 30;
                const tierMonthlyRewardsValue = tierMonthlyRewards * effectivePrice;
                const tierMonthlySelfHostedCost = config.selfHostedElectricity + config.selfHostedInternet +
                                                  config.coolingCost + config.fixedIpCost + (config.serverCost * config.maintenancePercent / 100 / 12);
                const tierNetMonthlyProfit = tierMonthlyRewardsValue - tierMonthlySelfHostedCost;
                const tierROI = tierCollateral * config.avgPurchasePrice > 0
                  ? ((tierNetMonthlyProfit * 12) / (tierCollateral * config.avgPurchasePrice + config.serverCost + config.upsCost)) * 100
                  : 0;

                const isSelected = tier === config.tier;
                const isBest = tierROI === Math.max(
                  ...(['CUMULUS', 'NIMBUS', 'STRATUS'] as NodeTier[]).map(t => {
                    const nodes = Math.floor(calculations.totalCollateral / NODE_TIERS[t].collateral);
                    if (nodes === 0) return -Infinity;
                    const reward = realRewards?.byTier?.[t]?.daily || (t === 'CUMULUS' ? 2 : t === 'NIMBUS' ? 5 : 8);
                    const monthly = reward * nodes * 30 * effectivePrice;
                    const costs = config.selfHostedElectricity + config.selfHostedInternet + config.coolingCost + config.fixedIpCost + (config.serverCost * config.maintenancePercent / 100 / 12);
                    return ((monthly - costs) * 12) / (NODE_TIERS[t].collateral * nodes * config.avgPurchasePrice + config.serverCost + config.upsCost) * 100;
                  })
                );

                return (
                  <div
                    key={tier}
                    style={{
                      padding: '16px',
                      background: isSelected ? `${tierConfig.color}15` : 'var(--card-bg)',
                      border: `2px solid ${isSelected ? tierConfig.color : isBest ? '#22c55e' : 'transparent'}`,
                      borderRadius: '12px',
                      position: 'relative'
                    }}
                  >
                    {isBest && (
                      <div style={{
                        position: 'absolute',
                        top: '-10px',
                        right: '10px',
                        background: '#22c55e',
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '10px',
                        fontWeight: 700
                      }}>
                        MEILLEUR ROI
                      </div>
                    )}

                    <div style={{
                      fontSize: '14px',
                      fontWeight: 700,
                      color: tierConfig.color,
                      marginBottom: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: tierConfig.color
                      }} />
                      {equivalentNodes}× {tier}
                    </div>

                    <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '8px' }}>
                      Collatéral: {formatFlux(tierCollateral)}
                    </div>

                    <div style={{ display: 'grid', gap: '6px', fontSize: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Rewards/mois:</span>
                        <span style={{ fontWeight: 600 }}>{formatFlux(tierMonthlyRewards)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Valeur:</span>
                        <span style={{ fontWeight: 600, color: '#10b981' }}>{formatCurrency(tierMonthlyRewardsValue, '$')}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Héberg. tiers:</span>
                        <span style={{ fontWeight: 600, color: '#ef4444' }}>-{formatCurrency(tierHostingCost)}/mois</span>
                      </div>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        paddingTop: '8px',
                        borderTop: '1px solid var(--border)',
                        marginTop: '4px'
                      }}>
                        <span>ROI annuel:</span>
                        <span style={{
                          fontWeight: 700,
                          fontSize: '14px',
                          color: tierROI >= 0 ? '#10b981' : '#ef4444'
                        }}>
                          {tierROI.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            </div>
            )}
          </div>

          {/* Risk Analysis Section */}
          <div style={{
            background: 'rgba(245, 158, 11, 0.08)',
            borderRadius: '12px',
            marginBottom: '24px',
            overflow: 'hidden',
            border: '1px solid rgba(245, 158, 11, 0.15)'
          }}>
            <div
              onClick={() => toggleSection('risk')}
              style={{
                padding: '16px 20px',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                userSelect: 'none'
              }}
            >
              <h3 style={{ fontSize: '16px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px', margin: 0, color: '#f59e0b' }}>
                ⚠️ Analyse des Risques
              </h3>
              <span style={{
                transition: 'transform 0.2s',
                transform: sections.risk ? 'rotate(0deg)' : 'rotate(-90deg)',
                color: 'var(--muted)'
              }}>
                <Icons.ChevronDown size={18} />
              </span>
            </div>
            {sections.risk && (
            <div style={{ padding: '0 20px 20px' }}>

            {(() => {
              // Calculate risk metrics
              const priceMargin = effectivePrice > 0 ? ((effectivePrice - calculations.priceThreshold) / effectivePrice) * 100 : 0;
              const priceRiskScore = priceMargin < 0 ? 100 : priceMargin < 20 ? 80 : priceMargin < 50 ? 50 : priceMargin < 100 ? 30 : 10;

              const downtimeRiskPerDay = calculations.dailyRewardsValue;
              const monthlyDowntimeRisk = downtimeRiskPerDay * 7; // 7 days downtime scenario

              const hardwareAge = 0; // Assuming new hardware
              const hardwareLifespan = 36; // 3 years
              const hardwareRiskScore = ((hardwareAge / hardwareLifespan) * 100);

              const rewardVolatilityRisk = config.rewardDecreasePercent > 15 ? 80 : config.rewardDecreasePercent > 10 ? 50 : 30;

              const overallRiskScore = Math.round((priceRiskScore * 0.4 + rewardVolatilityRisk * 0.3 + hardwareRiskScore * 0.2 + (calculations.breakEvenMonths > 24 ? 70 : calculations.breakEvenMonths > 12 ? 40 : 20) * 0.1));

              const getRiskColor = (score: number) => score >= 70 ? '#ef4444' : score >= 40 ? '#f59e0b' : '#10b981';
              const getRiskLabel = (score: number) => score >= 70 ? 'Élevé' : score >= 40 ? 'Modéré' : 'Faible';

              return (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                  {/* Overall Risk Score */}
                  <div style={{
                    padding: '16px',
                    background: 'var(--card-bg)',
                    borderRadius: '12px',
                    textAlign: 'center',
                    gridColumn: 'span 1'
                  }}>
                    <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '8px' }}>
                      Score de Risque Global
                    </div>
                    <div style={{
                      width: '80px',
                      height: '80px',
                      borderRadius: '50%',
                      border: `6px solid ${getRiskColor(overallRiskScore)}`,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto',
                      background: `${getRiskColor(overallRiskScore)}15`
                    }}>
                      <span style={{ fontSize: '24px', fontWeight: 700, color: getRiskColor(overallRiskScore) }}>
                        {overallRiskScore}
                      </span>
                      <span style={{ fontSize: '10px', color: getRiskColor(overallRiskScore) }}>
                        /100
                      </span>
                    </div>
                    <div style={{
                      marginTop: '8px',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: getRiskColor(overallRiskScore)
                    }}>
                      Risque {getRiskLabel(overallRiskScore)}
                    </div>
                  </div>

                  {/* Price Risk */}
                  <div style={{
                    padding: '16px',
                    background: 'var(--card-bg)',
                    borderRadius: '12px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span style={{ fontSize: '12px', color: 'var(--muted)' }}>💰 Risque Prix</span>
                      <span style={{
                        fontSize: '12px',
                        fontWeight: 600,
                        padding: '2px 8px',
                        borderRadius: '4px',
                        background: `${getRiskColor(priceRiskScore)}20`,
                        color: getRiskColor(priceRiskScore)
                      }}>
                        {getRiskLabel(priceRiskScore)}
                      </span>
                    </div>
                    <div style={{
                      height: '6px',
                      background: 'var(--border)',
                      borderRadius: '3px',
                      overflow: 'hidden',
                      marginBottom: '8px'
                    }}>
                      <div style={{
                        height: '100%',
                        width: `${priceRiskScore}%`,
                        background: getRiskColor(priceRiskScore),
                        borderRadius: '3px'
                      }} />
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
                      Marge avant seuil: {priceMargin > 0 ? '+' : ''}{priceMargin.toFixed(0)}%
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
                      Prix min rentable: ${calculations.priceThreshold.toFixed(4)}
                    </div>
                  </div>

                  {/* Downtime Risk */}
                  <div style={{
                    padding: '16px',
                    background: 'var(--card-bg)',
                    borderRadius: '12px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span style={{ fontSize: '12px', color: 'var(--muted)' }}>🔌 Risque Downtime</span>
                      <span style={{
                        fontSize: '12px',
                        fontWeight: 600,
                        padding: '2px 8px',
                        borderRadius: '4px',
                        background: 'rgba(245, 158, 11, 0.2)',
                        color: '#f59e0b'
                      }}>
                        Modéré
                      </span>
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>
                      Perte par jour offline: {formatCurrency(downtimeRiskPerDay, '$')}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>
                      Scénario 7j offline: {formatCurrency(monthlyDowntimeRisk, '$')}
                    </div>
                    <div style={{ fontSize: '10px', color: 'var(--muted)', fontStyle: 'italic' }}>
                      Conseil: UPS + monitoring 24/7
                    </div>
                  </div>

                  {/* Reward Volatility Risk */}
                  <div style={{
                    padding: '16px',
                    background: 'var(--card-bg)',
                    borderRadius: '12px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span style={{ fontSize: '12px', color: 'var(--muted)' }}>📉 Dilution Rewards</span>
                      <span style={{
                        fontSize: '12px',
                        fontWeight: 600,
                        padding: '2px 8px',
                        borderRadius: '4px',
                        background: `${getRiskColor(rewardVolatilityRisk)}20`,
                        color: getRiskColor(rewardVolatilityRisk)
                      }}>
                        {getRiskLabel(rewardVolatilityRisk)}
                      </span>
                    </div>
                    <div style={{
                      height: '6px',
                      background: 'var(--border)',
                      borderRadius: '3px',
                      overflow: 'hidden',
                      marginBottom: '8px'
                    }}>
                      <div style={{
                        height: '100%',
                        width: `${rewardVolatilityRisk}%`,
                        background: getRiskColor(rewardVolatilityRisk),
                        borderRadius: '3px'
                      }} />
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
                      Taux décroissance: {config.rewardDecreasePercent}%/an
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
                      Rewards An 3: {formatFlux(calculations.year3Rewards)}
                    </div>
                  </div>

                  {/* Breakeven Risk */}
                  <div style={{
                    padding: '16px',
                    background: 'var(--card-bg)',
                    borderRadius: '12px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span style={{ fontSize: '12px', color: 'var(--muted)' }}>🎯 Risque Amortissement</span>
                      <span style={{
                        fontSize: '12px',
                        fontWeight: 600,
                        padding: '2px 8px',
                        borderRadius: '4px',
                        background: `${getRiskColor(calculations.breakEvenMonths > 24 ? 70 : calculations.breakEvenMonths > 12 ? 40 : 20)}20`,
                        color: getRiskColor(calculations.breakEvenMonths > 24 ? 70 : calculations.breakEvenMonths > 12 ? 40 : 20)
                      }}>
                        {getRiskLabel(calculations.breakEvenMonths > 24 ? 70 : calculations.breakEvenMonths > 12 ? 40 : 20)}
                      </span>
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>
                      Point mort: {calculations.breakEvenMonths === Infinity ? '∞' : `${calculations.breakEvenMonths} mois`}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
                      Investissement: {formatCurrency(calculations.totalInvestment, '$')}
                    </div>
                    <div style={{ fontSize: '10px', color: 'var(--muted)', fontStyle: 'italic', marginTop: '4px' }}>
                      {calculations.breakEvenMonths <= 12 ? '✓ Excellent < 1 an' :
                       calculations.breakEvenMonths <= 24 ? '⚡ Bon < 2 ans' :
                       calculations.breakEvenMonths <= 36 ? '⚠️ Long terme' : '❌ Risqué'}
                    </div>
                  </div>
                </div>
              );
            })()}
            </div>
            )}
          </div>

          {/* Detailed Breakdown Table */}
          <div style={{
            background: 'rgba(236, 72, 153, 0.08)',
            borderRadius: '12px',
            marginBottom: '24px',
            overflow: 'hidden',
            border: '1px solid rgba(236, 72, 153, 0.15)'
          }}>
            <div
              onClick={() => toggleSection('details')}
              style={{
                padding: '16px 20px',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                userSelect: 'none'
              }}
            >
              <h3 style={{ fontSize: '16px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px', margin: 0, color: '#ec4899' }}>
                📋 Détail des Calculs
              </h3>
              <span style={{
                transition: 'transform 0.2s',
                transform: sections.details ? 'rotate(0deg)' : 'rotate(-90deg)',
                color: 'var(--muted)'
              }}>
                <Icons.ChevronDown size={18} />
              </span>
            </div>
            {sections.details && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: 'var(--card-bg)' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600 }}>Détail</th>
                  <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600 }}>Valeur</th>
                </tr>
              </thead>
              <tbody>
                {/* Collateral Section */}
                <tr style={{ background: 'rgba(139, 92, 246, 0.1)' }}>
                  <td colSpan={2} style={{ padding: '8px 16px', fontWeight: 600, color: '#8b5cf6' }}>
                    💎 Collatéral ({config.nodeCount} × {NODE_TIERS[config.tier].collateral} FLUX)
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '8px 16px' }}>Total Collatéral</td>
                  <td style={{ padding: '8px 16px', textAlign: 'right', fontFamily: 'monospace' }}>
                    {formatFlux(calculations.totalCollateral)}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '8px 16px' }}>Coût à l'achat (+ frais exchange)</td>
                  <td style={{ padding: '8px 16px', textAlign: 'right', fontFamily: 'monospace' }}>
                    {formatCurrency(calculations.collateralCostAtPurchase, '$')}
                    {calculations.exchangeFees > 0 && (
                      <span style={{ color: 'var(--muted)', fontSize: '11px', marginLeft: '4px' }}>
                        (dont {formatCurrency(calculations.exchangeFees, '$')} fees)
                      </span>
                    )}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '8px 16px' }}>Valeur actuelle</td>
                  <td style={{ padding: '8px 16px', textAlign: 'right', fontFamily: 'monospace' }}>
                    {formatCurrency(calculations.collateralCurrentValue, '$')}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '8px 16px' }}>P&L Collatéral</td>
                  <td style={{
                    padding: '8px 16px',
                    textAlign: 'right',
                    fontFamily: 'monospace',
                    color: calculations.collateralPnL >= 0 ? '#10b981' : '#ef4444',
                    fontWeight: 600
                  }}>
                    {formatCurrency(calculations.collateralPnL, '$')} ({calculations.collateralPnLPercent >= 0 ? '+' : ''}{calculations.collateralPnLPercent.toFixed(1)}%)
                  </td>
                </tr>

                {/* Hosting Section */}
                <tr style={{ background: 'rgba(16, 185, 129, 0.1)' }}>
                  <td colSpan={2} style={{ padding: '8px 16px', fontWeight: 600, color: '#10b981' }}>
                    🏠 Comparaison Hébergement (mensuel)
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '8px 16px' }}>Hébergement tiers ({config.nodeCount} nodes)</td>
                  <td style={{ padding: '8px 16px', textAlign: 'right', fontFamily: 'monospace', color: '#ef4444' }}>
                    -{formatCurrency(calculations.monthlyThirdPartyCost)}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '8px 16px' }}>Self-hosted (élec. + internet)</td>
                  <td style={{ padding: '8px 16px', textAlign: 'right', fontFamily: 'monospace', color: '#ef4444' }}>
                    -{formatCurrency(calculations.monthlySelfHostedCost)}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '8px 16px', fontWeight: 600 }}>Économies mensuelles</td>
                  <td style={{ padding: '8px 16px', textAlign: 'right', fontFamily: 'monospace', color: '#10b981', fontWeight: 600 }}>
                    +{formatCurrency(calculations.monthlyHostingSavings)}
                  </td>
                </tr>

                {/* Investment Section */}
                <tr style={{ background: 'rgba(99, 102, 241, 0.1)' }}>
                  <td colSpan={2} style={{ padding: '8px 16px', fontWeight: 600, color: '#6366f1' }}>
                    💼 Investissement Total
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '8px 16px' }}>Serveur</td>
                  <td style={{ padding: '8px 16px', textAlign: 'right', fontFamily: 'monospace' }}>
                    {formatCurrency(config.serverCost)}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '8px 16px' }}>UPS/Onduleur</td>
                  <td style={{ padding: '8px 16px', textAlign: 'right', fontFamily: 'monospace' }}>
                    {formatCurrency(config.upsCost)}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '8px 16px' }}>Total Matériel</td>
                  <td style={{ padding: '8px 16px', textAlign: 'right', fontFamily: 'monospace' }}>
                    {formatCurrency(calculations.totalHardwareCost)}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '8px 16px' }}>Collatéral (+ fees exchange)</td>
                  <td style={{ padding: '8px 16px', textAlign: 'right', fontFamily: 'monospace' }}>
                    {formatCurrency(calculations.collateralCostAtPurchase, '$')}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '8px 16px', fontWeight: 600 }}>Total Investissement</td>
                  <td style={{ padding: '8px 16px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>
                    {formatCurrency(calculations.totalInvestment, '$')}
                  </td>
                </tr>

                {/* Rewards Evolution Section */}
                <tr style={{ background: 'rgba(245, 158, 11, 0.1)' }}>
                  <td colSpan={2} style={{ padding: '8px 16px', fontWeight: 600, color: '#f59e0b' }}>
                    📉 Évolution Rewards (avec {config.rewardDecreasePercent}% décroissance/an)
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '8px 16px' }}>Année 1</td>
                  <td style={{ padding: '8px 16px', textAlign: 'right', fontFamily: 'monospace' }}>
                    {formatFlux(calculations.year1Rewards)} ≈ {formatCurrency(calculations.year1Rewards * effectivePrice, '$')}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '8px 16px' }}>Année 2</td>
                  <td style={{ padding: '8px 16px', textAlign: 'right', fontFamily: 'monospace' }}>
                    {formatFlux(calculations.year2Rewards)} ≈ {formatCurrency(calculations.year2Rewards * effectivePrice, '$')}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '8px 16px' }}>Année 3</td>
                  <td style={{ padding: '8px 16px', textAlign: 'right', fontFamily: 'monospace' }}>
                    {formatFlux(calculations.year3Rewards)} ≈ {formatCurrency(calculations.year3Rewards * effectivePrice, '$')}
                  </td>
                </tr>

                {/* Projection Section */}
                <tr style={{ background: 'rgba(236, 72, 153, 0.1)' }}>
                  <td colSpan={2} style={{ padding: '8px 16px', fontWeight: 600, color: '#ec4899' }}>
                    🔮 Projection 12 mois ({config.priceGrowthScenario === 'bearish' ? '🐻 Baissier' : config.priceGrowthScenario === 'neutral' ? '📊 Neutre' : '🐂 Haussier'})
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '8px 16px' }}>Prix FLUX projeté</td>
                  <td style={{ padding: '8px 16px', textAlign: 'right', fontFamily: 'monospace' }}>
                    ${calculations.projectedPrice.toFixed(4)}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '8px 16px' }}>Valeur collatéral projetée</td>
                  <td style={{ padding: '8px 16px', textAlign: 'right', fontFamily: 'monospace' }}>
                    {formatCurrency(calculations.projectedCollateralValue, '$')}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '8px 16px' }}>Rewards annuels (valeur projetée)</td>
                  <td style={{ padding: '8px 16px', textAlign: 'right', fontFamily: 'monospace' }}>
                    {formatCurrency(calculations.projectedYearlyRewardsValue, '$')}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '8px 16px', fontWeight: 600 }}>Profit Total Projeté (12 mois)</td>
                  <td style={{
                    padding: '8px 16px',
                    textAlign: 'right',
                    fontFamily: 'monospace',
                    fontWeight: 600,
                    color: calculations.projectedTotalProfit >= 0 ? '#10b981' : '#ef4444'
                  }}>
                    {formatCurrency(calculations.projectedTotalProfit, '$')}
                  </td>
                </tr>
              </tbody>
            </table>
            )}
          </div>

          {/* Disclaimer */}
          <div style={{
            marginTop: '16px',
            padding: '12px 16px',
            background: 'rgba(245, 158, 11, 0.1)',
            borderRadius: '8px',
            fontSize: '12px',
            color: 'var(--muted)'
          }}>
            ⚠️ <strong>Disclaimer</strong>: Ces calculs sont des estimations basées sur les données actuelles.
            Les rewards, le prix du FLUX et les coûts peuvent varier. Ne constitue pas un conseil en investissement.
          </div>
        </>
      )}
    </div>
  );
}
